import { useMainStore } from "./../main";
import { defineStore } from "pinia";
import {
    CoverDetails,
    PolicyDetails,
    ProposalState,
    Proposal,
    Quote,
    DirectDebit,
    UpdatePaymentChoicePayload,
    PropertyDetails,
    ResidencyDetails,
    Risk,
    Addons,
    MarketingPreferences
} from "./types/types";
import { EMPTY_RISK } from "./static";
import {
    EmploymentDetails,
    JointProposer,
    PersonalDetails,
    PreviousPolicyDetails,
    AdditionalCover,
    Proposer,
    Address
} from "./types/drivers/driver-types";
import axios, { AxiosError } from "axios";
import { ApiEndpoints } from "@/static/api-endpoints";
import { ROUTE_PATHS, ROUTE_NAMES, FORM_MODE } from "@/router/types";
import { routeGuard } from "@/router";
import router from "@/router";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { goToError, QuoteRecallErrorFields, QuoteRecallErrorMessages } from "@/utils/error-handling";
import { GA4Helper } from "@/utils/ga4-dataLayers";
import { RecallQuoteDetails } from "../types";
import { EndorsementCode } from "@/static/constants";

dayjs.extend(isSameOrBefore);

const EMPTY_PROPOSAL = {
    quote_id: null,
    risk: EMPTY_RISK,
    uuid: null,
    quote: {} as Quote,
    quotes: [],
    addons: {
        garden_cover: null,
        family_legal_protection: null
    },
    is_pending: false,
    is_quoted: false,
    is_purchased: false,
    is_pay_monthly: false,
    is_accepted: false,
    is_past_cover_start_date: null,
    direct_debit: {
        account_name: "",
        account_number: null,
        sort_code: null,
        iban: null,
        branch_name: null,
        bank_name: null,
        bic_code: null,
        address_line_1: null,
        address_line_2: null,
        address_line_3: null,
        address_line_4: null,
        eircode: null,
        ecad_id: null
    },
    payment_result_code: null,
    allow_general_marketing: null,
    allow_product_marketing: null,
    contact_preferences: {
        post: null,
        email: null,
        sms: null,
        mobile: null
    },
    quote_scheme_changed: null,
    recalled_from_offline: false,
    payment_attempts_exceeded: null,
    num_payment_attempts: null,
    payment_attempts_remaining: null
};

const proposal: Proposal = cloneDeep(EMPTY_PROPOSAL);

const state: ProposalState = {
    proposal: proposal,
    cachedQuotes: [],
    displayCachedQuotes: false,
    gettingProposal: true,
    payment: {
        url: null,
        amount: null
    },
    assumptions_accepted: null
};

export function getValidQuotes(quotes: Array<Quote>) {
    return quotes.filter(quote => quote.is_quoted);
}

export const useProposalStore = defineStore("risk<persistent>", {
    state: () => state,
    actions: {
        resetProposal() {
            this.$state.proposal = cloneDeep(EMPTY_PROPOSAL);
        },
        async getProposal(uuid: string) {
            const main = useMainStore();
            main.setLoadingState(true);
            try {
                if (uuid) {
                    this.gettingProposal = true;
                    const proposalResponse = await axios.get<{ proposal: Proposal }>(ApiEndpoints.GetProposal(uuid));
                    this.setProposal(proposalResponse.data.proposal);

                    const matched = router.currentRoute.value.matched ?? [];
                    if (!matched.find(route => route.name === FORM_MODE.EDIT) && this.validQuotes.length === 0) {
                        await router.push({ name: ROUTE_NAMES.Declined });
                    }
                }
            } catch (e) {
                throw e;
            } finally {
                routeGuard();
                this.gettingProposal = false;
                main.setLoadingState(false);
            }
        },
        async generateQuoteDocuments(uuid: string) {
            try {
                await axios.post(ApiEndpoints.GenerateQuoteDocuments(uuid));
            } catch (e) {
                throw e;
            }
        },
        async getQuoteDocuments(uuid: string) {
            try {
                if (uuid) {
                    const proposalResponse = await axios.get<{ proposal: Proposal }>(ApiEndpoints.GetProposal(uuid));
                    if (
                        proposalResponse.data.proposal.quote.quote_documents &&
                        Array.isArray(proposalResponse.data.proposal.quote.quote_documents) &&
                        proposalResponse.data.proposal.quote.quote_documents.length > 0
                    ) {
                        this.proposal.quote.quote_documents = proposalResponse.data.proposal.quote.quote_documents;
                    }
                }
            } catch (e) {
                throw e;
            }
        },
        async selectQuote(quoteId: number) {
            const quote = this.proposal.quotes.find(quote => quote.id === quoteId);
            if (quote) {
                const response = await axios.post<{ proposal: Proposal }>(
                    ApiEndpoints.SelectQuote(this.proposal.uuid ?? ""),
                    {
                        product: quote.product_scheme
                    }
                );
                this.proposal.quote = response.data.proposal.quote;
            }
        },
        async createProposal() {
            try {
                const response = await axios.post(ApiEndpoints.CreateProposal, {
                    allow_general_marketing: this.proposal.allow_general_marketing,
                    allow_product_marketing: this.proposal.allow_product_marketing,
                    risk: this.proposal.risk
                });
                const data = response?.data?.proposal;
                if (data && data.uuid) {
                    this.setProposal(data);
                }
            } catch (e) {
                throw e;
            }
        },
        async updateProposal() {
            const main = useMainStore();
            try {
                const response = await axios.post(ApiEndpoints.UpdateProposal(this.proposal.uuid ?? ""), {
                    allow_general_marketing: this.proposal.allow_general_marketing,
                    allow_product_marketing: this.proposal.allow_product_marketing,
                    addons: this.proposal.addons,
                    risk: this.proposal.risk
                });
                const data = response?.data?.proposal;
                if (data && data.uuid) {
                    this.setProposal(data);
                }
            } catch (e) {
                main.setLoadingState(false);
                throw e;
            }
        },
        async createQuote() {
            try {
                const response = await axios.post(ApiEndpoints.CreateQuote(this.proposal.uuid ?? ""));
                const data = response?.data?.proposal;

                if (data && data.uuid) {
                    this.setProposal(data);
                }

                if (getValidQuotes(data.quotes).length === 0) {
                    await router.push({ name: ROUTE_NAMES.Declined });
                }
            } catch (e) {
                throw e;
            }
        },
        async createProductQuote() {
            try {
                const response = await axios.post(ApiEndpoints.CreateProductQuote(this.proposal.uuid ?? ""));
                const data = response?.data?.proposal;

                if (data && data.uuid) {
                    this.setProposal(data);
                }

                if (getValidQuotes(data.quotes).length === 0) {
                    await router.push({ name: ROUTE_NAMES.Declined });
                }
            } catch (e) {
                throw e;
            }
        },
        async recalculateQuote(allInsurers = true, isPayMonthly: boolean | null) {
            const mainStore = useMainStore();
            this.cachedQuotes = this.proposal.quotes;
            this.displayCachedQuotes = true;
            mainStore.setLoadingStatePopupText("Recalculating quote...");
            mainStore.setLoadingState(true);
            await this.updateProposal();
            allInsurers ? await this.createQuote() : await this.createProductQuote();
            if (isPayMonthly !== null) await this.UpdatePaymentChoice(isPayMonthly);
            this.cachedQuotes = [];
            this.displayCachedQuotes = false;
            mainStore.setLoadingState(false);
        },
        async resetQuote() {
            const mainStore = useMainStore();
            mainStore.setLoadingStatePopupText("Taking you back to your previous quote");
            mainStore.setLoadingState(true);

            const resetResponse = await axios.post(ApiEndpoints.ResetProposal(this.proposal.uuid ?? ""));
            const resetData = resetResponse?.data?.proposal;

            if (resetData && resetData.uuid) {
                this.setProposal(resetData);
            }
            mainStore.setLoadingState(false);
        },
        async UpdatePaymentChoice(isPayMonthly: boolean) {
            await axios.post(ApiEndpoints.UpdatePaymentChoice(this.proposal.uuid ?? ""), {
                is_pay_monthly: isPayMonthly
            });
            this.proposal.is_pay_monthly = isPayMonthly;
        },
        async UpdatePaymentDetails(payload: UpdatePaymentChoicePayload) {
            try {
                this.proposal.is_pay_monthly = payload.is_pay_monthly;
                const data = {
                    is_pay_monthly: payload.is_pay_monthly,
                    direct_debit: {
                        ...this.proposal.direct_debit,
                        sort_code: payload.sort_code
                    }
                };
                await axios
                    .post(ApiEndpoints.UpdatePaymentDetails(this.proposal.uuid as string), data)
                    .then(response => {
                        if (response && response.data) this.proposal.direct_debit = response.data.proposal.direct_debit;
                    });
            } catch (e) {
                throw e;
            }
        },
        async getPaymentPageURL() {
            const response = await axios.post<{ payment_url: string; payment: { amount: string } }>(
                ApiEndpoints.GetPaymentPageURL(this.proposal.uuid ?? ""),
                {
                    return_url: `${window.location.origin}${ROUTE_PATHS.ThankYou}?uuid=${this.proposal.uuid}`,
                    back_url: `${window.location.origin}${ROUTE_PATHS.Payment}?uuid=${this.proposal.uuid}`
                }
            );

            this.payment = {
                url: response.data.payment_url,
                amount: response.data.payment.amount
            };
        },
        async recallQuote(recallDetails: RecallQuoteDetails) {
            const main = useMainStore();
            main.setLoadingState(true);
            main.setQuoteRecallErrors(QuoteRecallErrorFields.RESET);
            try {
                const response = await axios.post<{ proposal: Proposal }>(ApiEndpoints.RecallQuote, recallDetails);
                this.setProposal(response.data.proposal);

                const policyStartDate = this.proposal.risk.policy_details.start_date;
                const isOfflineQuote = this.proposal.recalled_from_offline;
                const isQuoteAccepted = this.proposal.is_accepted;
                const isPastCoverStartDate = this.proposal.is_past_cover_start_date;

                const validToDate = dayjs(this.proposal.quote.date_valid_to);
                const isQuoteExpired = validToDate.isBefore(dayjs());

                if (recallDetails.allow_expired) {
                    await router.push({
                        name: ROUTE_NAMES.MarketingPreferences,
                        query: {
                            uuid: response.data.proposal.uuid
                        }
                    });
                    return;
                }

                if (policyStartDate && !isQuoteAccepted) {
                    // Start date in the past
                    if (isPastCoverStartDate) {
                        if (!isQuoteExpired) {
                            // Start date in past, not expired
                            if (!isOfflineQuote) {
                                // Web quote - show update start date modal
                                main.displayStartDateIsInvalidModal = true;
                            } else {
                                // Offline quote - decline
                                await router.push({
                                    name: ROUTE_NAMES.Declined,
                                    query: {
                                        uuid: this.proposal.uuid ?? ""
                                    }
                                });
                            }
                        } else {
                            // Start date in past, expired
                            if (!isOfflineQuote) {
                                // Web quote - show expired modal
                                main.setDisplayQuoteExpiredModal(true);
                            } else {
                                // Offline quote - decline
                                await router.push({
                                    name: ROUTE_NAMES.Declined,
                                    query: {
                                        uuid: this.proposal.uuid ?? ""
                                    }
                                });
                            }
                        }
                    } else {
                        // Start date NOT in the past (future or today)
                        if (!isQuoteExpired) {
                            // Not expired - go to PPP (Your Quotes)
                            await router.push({
                                name: ROUTE_NAMES.YourQuotes,
                                query: {
                                    uuid: response.data.proposal.uuid
                                }
                            });
                        } else {
                            // Expired - show expired popup
                            main.setDisplayQuoteExpiredModal(true);
                        }
                    }
                }
            } catch (error: any) {
                main.setLoadingState(false);
                if (error instanceof AxiosError) {
                    const errorMessage = error?.response?.data?.message;

                    const errorHandlers: Record<string, () => void> = {
                        [QuoteRecallErrorMessages.NAME]: () => {
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.NAME);
                        },
                        [QuoteRecallErrorMessages.DOB]: () => {
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.DOB);
                        },
                        [QuoteRecallErrorMessages.REFERENCE]: () => {
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.REFERENCE);
                        },
                        [QuoteRecallErrorMessages.ACCEPTED]: () => {
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.ACCEPTED);
                            main.setDisplayQuoteAcceptedModal(true);
                        },
                        [QuoteRecallErrorMessages.EXPIRED]: () => {
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.EXPIRED);
                            main.setDisplayQuoteExpiredModal(true);
                        },
                        [QuoteRecallErrorMessages.EXPIRED_OFFLINE]: () => {
                            if (error.response?.data?.proposal) {
                                this.setProposal(error.response.data.proposal);
                            }
                            main.setQuoteRecallErrors(QuoteRecallErrorFields.EXPIRED_OFFLINE);
                            router.push({
                                name: ROUTE_NAMES.Declined
                            });
                        }
                    };

                    if (errorMessage && errorHandlers[errorMessage]) {
                        errorHandlers[errorMessage]();
                        return;
                    }
                    if (errorMessage && errorMessage === QuoteRecallErrorMessages.NOT_QUOTED) {
                        main.setQuoteRecallErrors(QuoteRecallErrorFields.NOT_QUOTED);
                        main.setDisplayNotQuotedModal(true);
                        return;
                    }
                    return;
                }
                goToError(this.proposal.uuid ?? "");
            } finally {
                main.setLoadingState(false);
            }
        },
        async updateMarketingPreferences() {
            const main = useMainStore();
            main.setLoadingStatePopupText("Saving marketing preferences...");
            main.setLoadingState(true);

            try {
                const response = await axios.post(ApiEndpoints.UpdateMarketingPreferences(this.proposal.uuid ?? ""), {
                    allow_general_marketing: this.proposal.allow_general_marketing,
                    allow_product_marketing: this.proposal.allow_product_marketing,
                    contact_preferences: this.proposal.contact_preferences
                });
                this.setProposal(response.data.proposal);

                await router.push({
                    name: ROUTE_NAMES.MarketingPreferencesSuccess,
                    query: {
                        uuid: this.proposal.uuid ?? ""
                    }
                });
            } catch (error) {
                throw error;
            } finally {
                main.setLoadingState(false);
            }
        },
        async confirmRisk({ uuid, risk, isMonthly }: { uuid: string; risk: Risk; isMonthly: boolean }) {
            try {
                const response = await axios.post(ApiEndpoints.ConfirmRisk(uuid), { risk, is_pay_monthly: isMonthly });
                this.proposal = response.data.proposal;
            } catch (e) {
                throw e;
            }
        },
        async updateDirectDebitDetails(details) {
            const main = useMainStore();
            main.setLoadingStatePopupText("Checking details...");
            main.setLoadingState(true);
            try {
                const response = await axios.post(ApiEndpoints.UpdatePaymentDetails(this.proposal.uuid ?? ""), details);
                let responseData = response.data.proposal;

                if (responseData && responseData.uuid) {
                    this.proposal = response.data.proposal;
                }
            } catch (e) {
                main.setLoadingState(false);
                throw e;
            } finally {
                main.setLoadingState(false);
            }
        },
        patchDirectDebitDetails(data: Partial<DirectDebit>) {
            this.proposal.direct_debit = {
                ...this.proposal.direct_debit,
                ...data,
                iban: data.iban?.toUpperCase() ?? null
            };
        },
        setProposal(proposal: Proposal) {
            this.proposal = proposal;
        },
        patchProposer(data: Partial<Proposer>) {
            this.proposal.risk.proposer = { ...this.proposal.risk.proposer, ...data };
        },
        patchPropertyDetails(data: Partial<PropertyDetails>) {
            this.proposal.risk.property_details = { ...this.proposal.risk.property_details, ...data };
        },
        patchResidencyDetails(data: Partial<ResidencyDetails>) {
            this.proposal.risk.residency_details = { ...this.proposal.risk.residency_details, ...data };
        },
        patchPolicyDetails(data: Partial<PolicyDetails>) {
            this.proposal.risk.policy_details = { ...this.proposal.risk.policy_details, ...data };
        },
        patchCoverDetails(data: Partial<CoverDetails>) {
            this.proposal.risk.cover_details = { ...this.proposal.risk.cover_details, ...data };
        },
        patchInterestedParties(hasInterestedParties: boolean | null, interestedParty?: string | null) {
            this.proposal.risk.has_interested_parties = hasInterestedParties;
            this.proposal.risk.interested_parties = [];
            if (interestedParty) {
                this.proposal.risk.interested_parties.push({
                    code: interestedParty
                });
            }
        },
        patchAdditionalCover(data: Partial<AdditionalCover>) {
            this.proposal.risk.cover_details.additional_cover = {
                ...this.proposal.risk.cover_details.additional_cover,
                ...data
            } as AdditionalCover;
        },
        patchPreviousPolicyDetails(data: Partial<PreviousPolicyDetails>) {
            this.proposal.risk.proposer.previous_policy_details = {
                ...this.proposal.risk.proposer.previous_policy_details,
                ...data
            } as PreviousPolicyDetails;
        },
        patchPersonalDetails(data: Partial<PersonalDetails>) {
            this.proposal.risk.proposer.personal_details = { ...this.proposal.risk.proposer.personal_details, ...data };
        },
        patchEmploymentDetails(data: Partial<EmploymentDetails>) {
            this.proposal.risk.proposer.employment_details = {
                ...this.proposal.risk.proposer.employment_details,
                ...data
            };
        },
        patchJointProposerDetails(data: {
            has_joint_proposers: boolean | null;
            joint_proposers: Array<JointProposer>;
        }) {
            this.proposal.risk.has_joint_proposers = data.has_joint_proposers;
            this.proposal.risk.joint_proposers = data.joint_proposers;
        },
        patchAssumptionsAccepted(isAccepted: boolean) {
            this.assumptions_accepted = isAccepted;
        },
        patchGeneralMarketing(data: boolean | null) {
            this.proposal.allow_general_marketing = data;
        },
        patchProductMarketing(data: boolean | null) {
            this.proposal.allow_product_marketing = data;
        },
        patchAddress(address: Address) {
            this.proposal.risk.property_details.address = {
                ...this.proposal.risk.property_details.address,
                ...address
            };
        },
        patchAddons(data: Addons) {
            this.proposal.addons = { ...this.proposal.addons, ...data };
        },
        toggleIsPayMonthly() {
            this.proposal.is_pay_monthly = !this.proposal.is_pay_monthly;
        },
        async quoteWrapUp(uuid) {
            const main = useMainStore();
            main.setLoadingState(true);
            try {
                if (this.proposal.is_accepted) {
                    return;
                }
                const response = await axios.post(ApiEndpoints.AcceptQuote(uuid));
                this.proposal = response.data.proposal;
                await GA4Helper.trackPageView({
                    eventName: "purchase",
                    pageTitle: ROUTE_NAMES.ThankYou,
                    pagePath: ROUTE_PATHS.ThankYou
                });
            } catch (e) {
                main.setLoadingState(false);
                throw e;
            } finally {
                main.setLoadingState(false);
            }
        },
        patchMarketingPreferences(preferences: MarketingPreferences) {
            this.proposal.allow_general_marketing = preferences.allow_general_marketing;
            this.proposal.allow_product_marketing = preferences.allow_product_marketing;
            this.proposal.contact_preferences = preferences.contact_preferences;
        }
    },
    getters: {
        validQuotes(state) {
            const validQuotes = state.proposal.quotes.filter(quote => {
                return quote.is_quoted === 1;
            });
            validQuotes.sort(sortQuotes);
            return validQuotes;
        },
        getQuoteById(state) {
            const quoteSource = this.displayCachedQuotes ? this.cachedQuotes : state.proposal.quotes;
            return (id: number) => quoteSource.find(quote => quote.id === id);
        },
        getEndorsementByCode(state) {
            const quote = state.proposal.quote;
            const endorsements = quote?.endorsements ?? [];
            return (code: EndorsementCode) => endorsements.find(endorsement => endorsement.code === code);
        }
    }
});

function sortQuotes(a: Quote, b: Quote) {
    const floatA = parseFloat(a.premium ?? "0");
    const floatB = parseFloat(b.premium ?? "0");

    return floatA - floatB;
}
